import {format} from 'date-fns'

import {parseApiDate} from '../jobs/job-schedule-format'
import type {DispatchResource, DispatchTimelineData, JobRequirement, UnassignedJob} from './types'

type EquipmentMatchTarget = Pick<
    DispatchResource,
    'name' | 'rentalAssetTypeId' | 'rentalAssetTypeName'
>

function normalizeEquipmentText(value: string): string {
    return value.trim().toLowerCase()
}

function wordsMatchLabel(label: string, target: string): boolean {
    const normalizedLabel = normalizeEquipmentText(label)
    const normalizedTarget = normalizeEquipmentText(target)
    if (!normalizedLabel || !normalizedTarget) {
        return false
    }

    const labelWords = normalizedLabel.split(/\s+/).filter(Boolean)
    return labelWords.every((word) => normalizedTarget.includes(word))
}

/** Whether a rental asset satisfies a job equipment requirement. */
export function equipmentMatchesRequirement(
    requirement: Pick<JobRequirement, 'label' | 'rentalAssetTypeId'>,
    asset: EquipmentMatchTarget,
): boolean {
    if (
        requirement.rentalAssetTypeId &&
        asset.rentalAssetTypeId &&
        requirement.rentalAssetTypeId === asset.rentalAssetTypeId
    ) {
        return true
    }

    if (
        asset.rentalAssetTypeName &&
        wordsMatchLabel(requirement.label, asset.rentalAssetTypeName)
    ) {
        return true
    }

    return wordsMatchLabel(requirement.label, asset.name)
}

export const OUT_OF_SERVICE_BACK_PREFIX = 'Out of service · back '

export function bookingFirstDay(
    job: Pick<UnassignedJob, 'schedule'>,
    bookingDays?: {from: string} | null,
): string | null {
    return bookingDays?.from ?? job.schedule?.startDate ?? null
}

export function equipmentOutOfServiceFor(
    asset: Pick<DispatchResource, 'outOfService'>,
    firstDay: string | null | undefined,
): boolean {
    if (!asset.outOfService) {
        return false
    }

    const back = asset.outOfService.expectedBack
    return !(back && firstDay && firstDay > back)
}

/** True when the asset's active rental on another project overlaps the job schedule. */
export function equipmentOnRentElsewhere(
    asset: Pick<DispatchResource, 'onRent'>,
    job: Pick<UnassignedJob, 'id' | 'schedule'>,
    data: Pick<DispatchTimelineData, 'jobIdentities'>,
): boolean {
    const onRent = asset.onRent
    if (!onRent || onRent.projectId === data.jobIdentities?.[job.id]?.projectId) {
        return false
    }

    const lastDay = job.schedule?.dateTbc ? null : job.schedule?.endDate
    return lastDay == null || lastDay >= onRent.since
}

export function equipmentCandidateReasons(asset: DispatchResource): string[] {
    const reasons: string[] = []
    if (asset.locationLabel) {
        reasons.push(`At ${asset.locationLabel}`)
    }
    if (asset.outOfService?.expectedBack) {
        reasons.push(
            `${OUT_OF_SERVICE_BACK_PREFIX}${format(parseApiDate(asset.outOfService.expectedBack), 'EEE MMM d')}`,
        )
    } else if (asset.outOfService) {
        reasons.push(asset.outOfService.reason)
    }
    return reasons
}

export function bulkEquipmentCandidateReasons(assets: DispatchResource[]): string[] {
    const locations = [
        ...new Set(assets.map((asset) => asset.locationLabel).filter((label) => label != null)),
    ]
    if (locations.length === 1) {
        return [`${assets.length} available · ${locations[0]}`]
    }
    if (locations.length > 1) {
        return [`${assets.length} available across ${locations.length} locations`]
    }
    return [`${assets.length} available`]
}
